import React, { useState, Component } from 'react';
import { SettingsIcon, ShieldCheckIcon, UsersIcon, KeyRoundIcon, PlusIcon, PencilIcon, TrashIcon, CheckIcon, LockIcon, FingerprintIcon, ScrollTextIcon, UserCogIcon } from 'lucide-react';
type SubTab = 'gerer' | 'analytiques' | 'configurer';
type GererSection = 'roles' | 'autorisations' | 'utilisateurs';
type ConfigSection = 'securite' | 'authentification' | 'journalisation';
interface Role {
  id: string;
  nom: string;
  description: string;
  statut: 'actif' | 'inactif';
}
interface Utilisateur {
  id: string;
  nom: string;
  email: string;
  roleId: string;
}
const ROLES: Role[] = [{
  id: 'admin',
  nom: 'Administrateur',
  description: 'Accès complet à tous les modules et paramètres',
  statut: 'actif'
}, {
  id: 'comm',
  nom: 'Gestionnaire Communications',
  description: 'Création et diffusion des avis municipaux',
  statut: 'actif'
}, {
  id: 'agent',
  nom: 'Agent Requêtes',
  description: 'Traitement des requêtes citoyennes',
  statut: 'actif'
}, {
  id: 'lecture',
  nom: 'Lecture seule',
  description: 'Consultation des données sans modification',
  statut: 'inactif'
}];
const MODULES: {
  id: string;
  label: string;
}[] = [{
  id: 'communications',
  label: 'Communications'
}, {
  id: 'requetes',
  label: 'Requêtes'
}, {
  id: 'id',
  label: 'ID'
}, {
  id: 'collectes',
  label: 'Collectes'
}, {
  id: 'compteurs',
  label: 'Compteurs'
}, {
  id: 'atna',
  label: 'ATNA AI'
}, {
  id: 'parametres',
  label: 'Paramètres'
}];
const ACTIONS = ['Voir', 'Créer', 'Modifier', 'Supprimer'] as const;
const INITIAL_USERS: Utilisateur[] = [{
  id: '1',
  nom: 'Hugo Langlais',
  email: 'hugo.langlais@villedewindsor.qc.ca',
  roleId: 'admin'
}, {
  id: '2',
  nom: 'Marie Tremblay',
  email: 'marie.tremblay@villedewindsor.qc.ca',
  roleId: 'comm'
}, {
  id: '3',
  nom: 'Jean Dupont',
  email: 'jean.dupont@villedewindsor.qc.ca',
  roleId: 'agent'
}, {
  id: '4',
  nom: 'Sarah Connor',
  email: 'sarah.connor@villedewindsor.qc.ca',
  roleId: 'agent'
}, {
  id: '5',
  nom: 'Belanor Waesceran',
  email: 'belanor.w@villedewindsor.qc.ca',
  roleId: 'lecture'
}];
// Default permissions per role
function buildInitialPermissions(): Record<string, boolean> {
  const perms: Record<string, boolean> = {};
  MODULES.forEach(m => {
    ACTIONS.forEach(a => {
      // admin: everything; lecture: only Voir; others: Voir+Créer+Modifier on a couple modules
      perms[`admin:${m.id}:${a}`] = true;
      perms[`lecture:${m.id}:${a}`] = a === 'Voir';
      perms[`comm:${m.id}:${a}`] = m.id === 'communications' ? a !== 'Supprimer' : a === 'Voir';
      perms[`agent:${m.id}:${a}`] = m.id === 'requetes' ? a !== 'Supprimer' : a === 'Voir';
    });
  });
  return perms;
}
interface SwitchProps {
  checked: boolean;
  onChange: () => void;
}
function Switch({
  checked,
  onChange
}: SwitchProps) {
  return <button type="button" role="switch" aria-checked={checked} onClick={onChange} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${checked ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-5378" data-mp="31xu7zpd1riqj">
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-5379" data-mp="3ksra8vkfupvs" />
    </button>;
}
export function SettingsModule() {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('gerer');
  const [gererSection, setGererSection] = useState<GererSection>('roles');
  const [configSection, setConfigSection] = useState<ConfigSection>('securite');
  const [roles] = useState<Role[]>(ROLES);
  const [users, setUsers] = useState<Utilisateur[]>(INITIAL_USERS);
  const [permissions, setPermissions] = useState<Record<string, boolean>>(buildInitialPermissions);
  const [selectedRole, setSelectedRole] = useState<string>('admin');
  const tabs: {
    id: SubTab;
    label: string;
  }[] = [{
    id: 'gerer',
    label: 'Gérer'
  }, {
    id: 'analytiques',
    label: 'Analytiques'
  }, {
    id: 'configurer',
    label: 'Configurer'
  }];
  const gererSections: {
    id: GererSection;
    label: string;
    icon: ComponentType<{
      className?: string;
    }>;
  }[] = [{
    id: 'roles',
    label: 'Rôles',
    icon: ShieldCheckIcon
  }, {
    id: 'autorisations',
    label: 'Autorisations',
    icon: KeyRoundIcon
  }, {
    id: 'utilisateurs',
    label: 'Utilisateurs',
    icon: UsersIcon
  }];
  const roleName = (id: string) => roles.find(r => r.id === id)?.nom ?? '—';
  const countPerms = (roleId: string) => Object.keys(permissions).filter(k => k.startsWith(`${roleId}:`) && permissions[k]).length;
  const countUsers = (roleId: string) => users.filter(u => u.roleId === roleId).length;
  const togglePerm = (key: string) => setPermissions(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  // ---------- GÉRER ----------
  const renderRoles = () => <div className="space-y-4" data-id="element-5380" data-mp="2yrnw4a4jyqmw">
      <div className="flex items-center justify-between" data-id="element-5381" data-mp="2uq2mcxnbesgx">
        <div data-id="element-5382" data-mp="2urnfqjxw85d7">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5383" data-mp="2uo9fbh8si68e">Rôles</h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-5384" data-mp="2pqg4gawjko1s">
            Définissez des rôles puis attribuez-leur des autorisations.
          </p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-5385" data-mp="2ftbhw9ysaqkg">
          <PlusIcon className="w-4 h-4" data-id="element-5386" data-mp="j00ahauzokje" /> Nouveau rôle
        </button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-5387" data-mp="e304ezy53hsa">
        <table className="w-full text-left" data-id="element-5388" data-mp="28hnh9qf6jloq">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5389" data-mp="28fu6q01g4hbg">
            <tr data-id="element-5390" data-mp="2d9f6nxmlnlda">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5391" data-mp="2d9yg0py6p1fp">
                Rôle
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-5392" data-mp="23ikhake245nm">
                Autorisations
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-5393" data-mp="2r6bv5sf8wqr0">
                Usagers
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5394" data-mp="2i6osz4eozgww">
                Statut
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right" data-id="element-5395" data-mp="2mbenopxm6pg8">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5396" data-mp="1avhqkefmgz7x">
            {roles.map(r => <tr key={r.id} className="hover:bg-slate-50" data-id="element-5397" data-mp="1392pcy73rkjg">
                <td className="px-4 py-3" data-id="element-5398" data-mp="yc2m0v1n0vw1">
                  <div className="font-semibold text-slate-900 text-sm" data-id="element-5399" data-mp="ybazsatxkaif">
                    {r.nom}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5400" data-mp="189qqicp1phre">
                    {r.description}
                  </div>
                </td>
                <td className="px-4 py-3 text-center text-sm font-medium text-slate-700" data-id="element-5401" data-mp="kl7h1tu9eyxq">
                  {countPerms(r.id)}
                </td>
                <td className="px-4 py-3 text-center text-sm font-medium text-slate-700" data-id="element-5402" data-mp="fj8e2l2fm41b">
                  {countUsers(r.id)}
                </td>
                <td className="px-4 py-3" data-id="element-5403" data-mp="pi7ni5v0u3pd">
                  <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border ${r.statut === 'actif' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-id="element-5404" data-mp="phee0x4oh03j">
                    {r.statut}
                  </span>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-5405" data-mp="3cg9d342buiui">
                  <div className="flex items-center justify-end gap-2" data-id="element-5406" data-mp="3hf2evz894leu">
                    <button onClick={() => {
                  setSelectedRole(r.id);
                  setGererSection('autorisations');
                }} className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" title="Configurer les autorisations" data-id="element-5407" data-mp="3hh6tpc7ov4tw">
                      <KeyRoundIcon className="w-4 h-4" data-id="element-5408" data-mp="42a6l8hqbf2r" />
                    </button>
                    <button className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" title="Modifier" data-id="element-5409" data-mp="8y7ldadcgddf">
                      <PencilIcon className="w-4 h-4" data-id="element-5410" data-mp="hxm94224m9u8" />
                    </button>
                    <button className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Supprimer" data-id="element-5411" data-mp="22j18jqed2m2y">
                      <TrashIcon className="w-4 h-4" data-id="element-5412" data-mp="1tj5r53u7puuh" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  const renderAutorisations = () => <div className="space-y-5" data-id="element-5413" data-mp="2h6dwflrn53wi">
      <div data-id="element-5414" data-mp="2h75paotusnj3">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5415" data-mp="2h8qmkm7jykkn">Autorisations</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-5416" data-mp="2cemc3sz2vuet">
          Choisissez un rôle et accordez-lui des autorisations par module.
        </p>
      </div>

      {/* Role selector */}
      <div className="flex flex-wrap gap-2" data-id="element-5417" data-mp="34wgo40rn87ul">
        {roles.map(r => <button key={r.id} onClick={() => setSelectedRole(r.id)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${selectedRole === r.id ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-5418" data-mp="305rt4bgr8sqp">
            {r.nom}
          </button>)}
      </div>

      {/* Permission matrix */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-5419" data-mp="s0iepmam6qli">
        <table className="w-full text-left" data-id="element-5420" data-mp="160kun2zd39y4">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5421" data-mp="15zi7n655trnh">
            <tr data-id="element-5422" data-mp="1118botbm2n0t">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5423" data-mp="111qbu11e9le0">
                Module
              </th>
              {ACTIONS.map(a => <th key={a} className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center w-28" data-id="element-5424" data-mp="1at4bc8tcil75">
                  {a}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5425" data-mp="23e1basjuehtg">
            {MODULES.map(m => <tr key={m.id} className="hover:bg-slate-50" data-id="element-5426" data-mp="28ddyjeemlqwk">
                <td className="px-4 py-3 text-sm font-medium text-slate-800" data-id="element-5427" data-mp="28bjlem9lj331">
                  {m.label}
                </td>
                {ACTIONS.map(a => {
              const key = `${selectedRole}:${m.id}:${a}`;
              return <td key={a} className="px-4 py-3 text-center" data-id="element-5428" data-mp="1kns0iu9zphq5">
                      <div className="flex justify-center" data-id="element-5429" data-mp="1pertxpjwhhnw">
                        <Switch checked={!!permissions[key]} onChange={() => togglePerm(key)} data-id="element-5430" data-mp="1pgcr7mxlnepg" />
                      </div>
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end" data-id="element-5431" data-mp="16oaegmo2wikh">
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-5432" data-mp="16n8s3m1m49ng">
          Enregistrer les autorisations
        </button>
      </div>
    </div>;
  const renderUtilisateurs = () => <div className="space-y-4" data-id="element-5433" data-mp="33kwkrk2db30m">
      <div data-id="element-5434" data-mp="2ymv415sezfsb">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-5435" data-mp="2yooaajz47xzs">Utilisateurs</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-5436" data-mp="2umkytvcg4tei">
          Assignez un rôle à chaque utilisateur.
        </p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-5437" data-mp="3rb93zr32cm6i">
        <table className="w-full text-left" data-id="element-5438" data-mp="3mcz6h9tv99hq">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-5439" data-mp="3me0ltqhx0i59">
            <tr data-id="element-5440" data-mp="3iakkwhytbvmr">
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5441" data-mp="3ia2j75tdsp7g">
                Utilisateur
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-5442" data-mp="26w76s4tv2ujx">
                Courriel
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide w-64" data-id="element-5443" data-mp="21vsvmdwyr4ho">
                Rôle assigné
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5444" data-mp="mf7oo4affy5n">
            {users.map(u => <tr key={u.id} className="hover:bg-slate-50" data-id="element-5445" data-mp="qeym2quglcj5">
                <td className="px-4 py-3" data-id="element-5446" data-mp="v6q42czju9xo">
                  <div className="flex items-center gap-3" data-id="element-5447" data-mp="v7jf3q5jjllm">
                    <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xs font-bold" data-id="element-5448" data-mp="106309ocb8tcx">
                      {u.nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <span className="text-sm font-medium text-slate-800" data-id="element-5449" data-mp="1e4av9qpsdklg">
                      {u.nom}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-5450" data-mp="3fwfk2tbzo1ry">{u.email}</td>
                <td className="px-4 py-3" data-id="element-5451" data-mp="2kmsic0k9um1g">
                  <select value={u.roleId} onChange={e => setUsers(prev => prev.map(x => x.id === u.id ? {
                ...x,
                roleId: e.target.value
              } : x))} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm cursor-pointer focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-5452" data-mp="2klhanpnpgu52">
                    {roles.map(r => <option key={r.id} value={r.id} data-id="element-5453" data-mp="26tklnuhc30cw">
                        {r.nom}
                      </option>)}
                  </select>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  // ---------- ANALYTIQUES ----------
  const renderAnalytiques = () => {
    const totalPerms = roles.reduce((acc, r) => acc + countPerms(r.id), 0);
    const kpis = [{
      label: 'Rôles actifs',
      value: String(roles.filter(r => r.statut === 'actif').length),
      icon: ShieldCheckIcon
    }, {
      label: 'Utilisateurs',
      value: String(users.length),
      icon: UsersIcon
    }, {
      label: 'Autorisations accordées',
      value: String(totalPerms),
      icon: KeyRoundIcon
    }, {
      label: 'Rôles configurés',
      value: String(roles.length),
      icon: UserCogIcon
    }];
    const maxUsers = Math.max(...roles.map(r => countUsers(r.id)), 1);
    return <div className="space-y-8" data-id="element-5454" data-mp="2o4ggecvw6eif">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5455" data-mp="2o354fpsam0g8">
          {kpis.map(k => {
          const Icon = k.icon;
          return <div key={k.label} className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5456" data-mp="yo4416j9b8rj">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center mb-3" data-id="element-5457" data-mp="tp1b2kgjwszx">
                  <Icon className="w-5 h-5" data-id="element-5458" data-mp="ovoki4sbac4j" />
                </div>
                <div className="text-2xl font-bold text-[#0F172A]" data-id="element-5459" data-mp="kveggxo2xvsb">
                  {k.value}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-5460" data-mp="fyeb6oz20x29">{k.label}</div>
              </div>;
        })}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-5461" data-mp="5yx7ue79yv9g">
          <h3 className="text-base font-bold text-[#0F172A] mb-4" data-id="element-5462" data-mp="60ztyn5b448w">
            Répartition des utilisateurs par rôle
          </h3>
          <div className="space-y-3" data-id="element-5463" data-mp="20cob8vz0giwg">
            {roles.map(r => {
            const c = countUsers(r.id);
            return <div key={r.id} className="flex items-center gap-3" data-id="element-5464" data-mp="1wcnxzennedqz">
                  <span className="text-sm text-slate-600 w-56 truncate" data-id="element-5465" data-mp="3e9hneraiesbx">
                    {r.nom}
                  </span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-5466" data-mp="3j6rj2oohs087">
                    <div className="h-full bg-[#4F46E5] rounded-full" style={{
                  width: `${c / maxUsers * 100}%`
                }} data-id="element-5467" data-mp="3nzu428f7e1cs"></div>
                  </div>
                  <span className="text-sm font-semibold text-slate-700 w-6 text-right" data-id="element-5468" data-mp="3096uzjtjx5on">
                    {c}
                  </span>
                </div>;
          })}
          </div>
        </div>
      </div>;
  };
  // ---------- CONFIGURER ----------
  const configSections: {
    id: ConfigSection;
    label: string;
    icon: ComponentType<{
      className?: string;
    }>;
    desc: string;
  }[] = [{
    id: 'securite',
    label: 'Sécurité',
    icon: LockIcon,
    desc: 'Politique de mot de passe et de session'
  }, {
    id: 'authentification',
    label: 'Authentification',
    icon: FingerprintIcon,
    desc: 'Méthodes de connexion et MFA'
  }, {
    id: 'journalisation',
    label: 'Journalisation',
    icon: ScrollTextIcon,
    desc: 'Audit et conservation des journaux'
  }];
  const [mfa, setMfa] = useState(true);
  const [sso, setSso] = useState(false);
  const [auditLog, setAuditLog] = useState(true);
  const renderConfigContent = () => {
    if (configSection === 'securite') {
      return <div className="space-y-5 max-w-lg" data-id="element-5469" data-mp="e0uuytaywue4">
          <div data-id="element-5470" data-mp="rx8e06v5h3up">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-5471" data-mp="rwyjc5z5u8io">
              Longueur minimale du mot de passe
            </label>
            <input type="number" defaultValue={12} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-5472" data-mp="2tpmv53pve4a2" />
          </div>
          <div data-id="element-5473" data-mp="2fl5qbvro0tky">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-5474" data-mp="2fkmix8zn5nl6">
              Expiration de la session (minutes)
            </label>
            <input type="number" defaultValue={30} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-5475" data-mp="25ujemcn2kn7j" />
          </div>
        </div>;
    }
    if (configSection === 'authentification') {
      return <div className="space-y-4 max-w-lg" data-id="element-5476" data-mp="20vgijhp2hrbp">
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-5477" data-mp="20t3ucmspukq7">
            <div data-id="element-5478" data-mp="1mxi5qgqea2yb">
              <p className="text-sm font-semibold text-slate-800" data-id="element-5479" data-mp="1ruibeqjc13ow">
                Authentification à deux facteurs (MFA)
              </p>
              <p className="text-xs text-slate-500" data-id="element-5480" data-mp="3harlurdg4mpj">
                Exiger un second facteur à la connexion.
              </p>
            </div>
            <Switch checked={mfa} onChange={() => setMfa(!mfa)} data-id="element-5481" data-mp="3m66wlag5q2hq" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-5482" data-mp="tm0dirp45e89">
            <div data-id="element-5483" data-mp="yg4odm1i26em">
              <p className="text-sm font-semibold text-slate-800" data-id="element-5484" data-mp="jp5jom3sc20b">
                Authentification unique (SSO)
              </p>
              <p className="text-xs text-slate-500" data-id="element-5485" data-mp="onguaz1wmgjl">
                Connexion via le fournisseur d'identité de la ville.
              </p>
            </div>
            <Switch checked={sso} onChange={() => setSso(!sso)} data-id="element-5486" data-mp="1cc011a5b2l9k" />
          </div>
        </div>;
    }
    return <div className="space-y-4 max-w-lg" data-id="element-5487" data-mp="13demdq2yfqe2">
        <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4" data-id="element-5488" data-mp="13ccz8n8nzdfz">
          <div data-id="element-5489" data-mp="17dey32yo5nhf">
            <p className="text-sm font-semibold text-slate-800" data-id="element-5490" data-mp="17cwwrrx5gj2n">
              Journal d'audit
            </p>
            <p className="text-xs text-slate-500" data-id="element-5491" data-mp="3o5ggu9ly1agn">
              Enregistrer toutes les actions des administrateurs.
            </p>
          </div>
          <Switch checked={auditLog} onChange={() => setAuditLog(!auditLog)} data-id="element-5492" data-mp="3kkmzwzsafcce" />
        </div>
        <div data-id="element-5493" data-mp="3fmv0zlt86pyp">
          <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-5494" data-mp="3flbidok3k2tr">
            Durée de conservation des journaux (jours)
          </label>
          <input type="number" defaultValue={365} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-5495" data-mp="3uh9jzmcshzdd" />
        </div>
      </div>;
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5496" data-mp="2wx6gynqwwty4">
      {/* Header */}
      <div className="mb-8" data-id="element-5497" data-mp="3blt7lllzp84n">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4" data-id="element-5498" data-mp="3bm2n2f9erww3">
          <div className="flex items-center gap-3" data-id="element-5499" data-mp="36sgn9nocfnhp">
            <div className="w-10 h-10 rounded-xl bg-[#0F172A] flex items-center justify-center" data-id="element-5500" data-mp="2j3xbuz9zyule">
              <SettingsIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-5501" data-mp="2e5vtyhob51bi" />
            </div>
            <div data-id="element-5502" data-mp="33thrjygsptb9">
              <h1 className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-5503" data-mp="33u193e2jafor">
                Paramètres
              </h1>
              <p className="text-sm text-slate-500" data-id="element-5504" data-mp="37u1pv5d3v2yz">
                Système d'autorisations par rôles
              </p>
            </div>
          </div>
          <nav aria-label="Sections" className="flex flex-wrap items-center gap-2" data-id="element-5505" data-mp="3hkwlh0c8rex3">
            {tabs.map(t => <button key={t.id} onClick={() => setActiveSubTab(t.id)} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${activeSubTab === t.id ? 'text-white bg-[#0F172A] shadow-sm hover:bg-[#1E293B]' : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-5506" data-mp="2kvyodbh0r4wr">
                {t.label}
              </button>)}
          </nav>
        </div>
      </div>

      {activeSubTab === 'analytiques' ? renderAnalytiques() : activeSubTab === 'configurer' ? <div className="flex flex-col lg:flex-row gap-6" data-id="element-5507" data-mp="1ykx4bjjnygcb">
          <div className="lg:w-60 flex-shrink-0" data-id="element-5508" data-mp="1tney40x16f80">
            <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4" data-id="element-5509" data-mp="1tnx6fvwywjwa">
              {configSections.map(s => {
            const Icon = s.icon;
            return <button key={s.id} onClick={() => setConfigSection(s.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${configSection === s.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-5510" data-mp="1jv982wqca7vg">
                    <Icon className={`w-4 h-4 ${configSection === s.id ? 'text-white' : 'text-slate-400'}`} data-id="element-5511" data-mp="110aqdo6jzmsj" />
                    {s.label}
                  </button>;
          })}
            </div>
          </div>
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-5512" data-mp="33e8b861j3khz">
            <div className="mb-6" data-id="element-5513" data-mp="38cjj4b89jkxk">
              <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5514" data-mp="38brpv7251zag">
                {configSections.find(s => s.id === configSection)?.label}
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-5515" data-mp="2yd2gk2ob5n5t">
                {configSections.find(s => s.id === configSection)?.desc}
              </p>
            </div>
            {renderConfigContent()}
            <div className="mt-8" data-id="element-5516" data-mp="2pegye8mr0a5k">
              <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-5517" data-mp="2pfieirimfmu5">
                Enregistrer
              </button>
            </div>
          </div>
        </div> :
    // GÉRER
    <div className="flex flex-col lg:flex-row gap-6" data-id="element-5518" data-mp="21ow5oy0slyj3">
          <div className="lg:w-60 flex-shrink-0" data-id="element-5519" data-mp="25pewqpuv30k8">
            <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4" data-id="element-5520" data-mp="25o5107bhe4gr">
              {gererSections.map(s => {
            const Icon = s.icon;
            return <button key={s.id} onClick={() => setGererSection(s.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${gererSection === s.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-5521" data-mp="1i3vv6i4if4sw">
                    <Icon className={`w-4 h-4 ${gererSection === s.id ? 'text-white' : 'text-slate-400'}`} data-id="element-5522" data-mp="21042drh31pqu" />
                    {s.label}
                  </button>;
          })}
              <div className="px-4 pt-3 pb-1" data-id="element-5523" data-mp="2at2x7r36aciw">
                <p className="text-[11px] text-slate-400 leading-relaxed" data-id="element-5524" data-mp="2orj9dxjt0v8f">
                  1. Créez un rôle · 2. Accordez des autorisations · 3.
                  Assignez-le à des usagers.
                </p>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8 min-w-0" data-id="element-5525" data-mp="1lfavw3071tno">
            {gererSection === 'roles' && renderRoles()}
            {gererSection === 'autorisations' && renderAutorisations()}
            {gererSection === 'utilisateurs' && renderUtilisateurs()}
          </div>
        </div>}
    </div>;
}